import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { api, formatApiErrorDetail, formatDate } from "../lib/api";

const STATUS = ["Baru", "Diproses", "Selesai"];

export default function AdminInbox() {
  const [data, setData] = useState({ items: [] });
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    setError("");
    const qs = new URLSearchParams();
    if (type) qs.set("type", type);
    if (status) qs.set("status", status);
    api.get(`/admin/messages?${qs}`)
      .then((r) => setData(r.data))
      .catch((err) => {
        setData({ items: [] });
        if (err.response?.status !== 401) setError(formatApiErrorDetail(err.response?.data?.detail));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, status]);

  const setStatusOf = async (mid, s) => {
    setError("");
    try {
      await api.put(`/admin/messages/${mid}`, { status: s });
      if (selected && selected.id === mid) setSelected({ ...selected, status: s });
      load();
    } catch (err) {
      if (err.response?.status !== 401) setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (mid) => {
    if (!window.confirm("Hapus pesan ini?")) return;
    setError("");
    try {
      await api.delete(`/admin/messages/${mid}`);
      setSelected(null);
      load();
    } catch (err) {
      if (err.response?.status !== 401) setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-inbox-page">
      <h1 className="font-serif text-3xl text-ink">Konsultasi &amp; Pesan</h1>

      <div className="flex gap-3 mt-6 flex-wrap">
        <select data-testid="inbox-type-filter" value={type} onChange={(e) => setType(e.target.value)} className="field-input !min-h-[40px] !w-auto">
          <option value="">Semua jenis</option>
          <option value="konsultasi">Konsultasi</option>
          <option value="kontak">Kontak</option>
        </select>
        <select data-testid="inbox-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="field-input !min-h-[40px] !w-auto">
          <option value="">Semua status</option>
          {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {error && <p role="alert" className="text-enji text-sm mt-5">{error}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <div className="bg-surface border border-line rounded-xl overflow-hidden">
          {loading ? (
            <p className="text-sub text-sm p-5">Memuat…</p>
          ) : data.items.length === 0 ? (
            <p className="text-faint text-sm p-5">Tidak ada pesan pada filter ini.</p>
          ) : (
            <ul className="divide-y divide-line">
              {data.items.map((m) => (
                <li key={m.id}>
                  <button
                    data-testid={`inbox-item-${m.id}`}
                    onClick={() => setSelected(m)}
                    className={`w-full text-left p-4 hover:bg-surface-muted/60 transition-colors ${selected?.id === m.id ? "bg-surface-muted" : ""}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-semibold text-ink text-[15px] truncate">{m.name}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold shrink-0 ${m.status === "Baru" ? "bg-enji text-white" : m.status === "Diproses" ? "bg-indigo/15 text-indigo" : "bg-moss/15 text-moss"}`}>{m.status}</span>
                    </div>
                    <p className="text-[13px] text-sub truncate mt-1">{m.topic || m.message}</p>
                    <p className="text-[12px] text-faint mt-1">{m.type === "konsultasi" ? "Konsultasi" : "Kontak"} · {formatDate(m.created_at)}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-surface border border-line rounded-xl p-6" data-testid="inbox-detail">
          {selected ? (
            <>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-serif text-2xl text-ink">{selected.name}</h2>
                  <a href={`mailto:${selected.email}`} className="text-enji text-sm underline underline-offset-2">{selected.email}</a>
                </div>
                <button onClick={() => del(selected.id)} data-testid="inbox-delete-button" aria-label="Hapus pesan" className="w-10 h-10 rounded-md border border-line text-sub hover:text-enji"><Trash2 size={16} className="mx-auto" /></button>
              </div>
              <dl className="mt-5 space-y-2 text-sm">
                {selected.whatsapp && <div><dt className="text-faint text-[12px] uppercase tracking-wide">WhatsApp</dt><dd className="text-ink mt-0.5">{selected.whatsapp}</dd></div>}
                {selected.topic && <div><dt className="text-faint text-[12px] uppercase tracking-wide">Topik</dt><dd className="text-ink mt-0.5">{selected.topic}</dd></div>}
                {selected.time_pref && <div><dt className="text-faint text-[12px] uppercase tracking-wide">Preferensi waktu</dt><dd className="text-ink mt-0.5">{selected.time_pref}</dd></div>}
                <div><dt className="text-faint text-[12px] uppercase tracking-wide">Pesan</dt><dd className="text-ink mt-0.5 whitespace-pre-wrap leading-relaxed">{selected.message}</dd></div>
                <div><dt className="text-faint text-[12px] uppercase tracking-wide">Notifikasi email</dt><dd className="mt-0.5">{selected.email_notify?.sent ? "Terkirim ke pemilik" : selected.email_notify?.error ? `Gagal: ${selected.email_notify.error}` : "Belum terkirim"}</dd></div>
              </dl>
              <div className="flex gap-2 mt-6 flex-wrap">
                {STATUS.map((s) => (
                  <button
                    key={s}
                    data-testid={`inbox-status-${s.toLowerCase()}`}
                    onClick={() => setStatusOf(selected.id, s)}
                    disabled={selected.status === s}
                    className={`px-4 h-10 rounded-full text-[13px] font-semibold transition-colors ${selected.status === s ? "bg-enji text-white" : "border border-line text-sub hover:text-ink"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <p className="text-faint text-sm">Pilih pesan dari daftar untuk melihat detail dan mengubah status.</p>
          )}
        </div>
      </div>
    </div>
  );
}
